import React, { useEffect, useState, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Download, CheckCircle2, XCircle, FileText, UploadCloud } from 'lucide-react';
import PageShell from '../components/layout/PageShell';

const ROLES_CLAIM = 'https://raah-portal.com/roles';
const API_URL = process.env.REACT_APP_API_URL;

const FILTER_OPTIONS = {
  product:  ['All', 'Okta WIC', 'Auth0'],
  industry: ['All', 'Financial Services', 'Healthcare', 'Retail', 'Technology', 'Government'],
  type:     ['All', 'Deck', 'One-pager', 'Case Study', 'Workshop'],
};

const FILTER_PLACEHOLDERS = {
  product: 'Select Platform',
  industry: 'Select Sector',
  type: 'Select Type',
};

// Card accent per material type — fixed categorical order (blue, aqua, yellow, green, violet),
// never cycled/reassigned. Type name is always rendered as text, so color never carries identity alone.
const TYPE_STYLES = {
  'Deck':                 { accent: '#2a78d6', tint: 'rgba(42,120,214,0.07)', chipTint: 'rgba(42,120,214,0.14)' },
  'One-pager':            { accent: '#1baf7a', tint: 'rgba(27,175,122,0.07)', chipTint: 'rgba(27,175,122,0.14)' },
  'Case Study':           { accent: '#eda100', tint: 'rgba(237,161,0,0.08)',  chipTint: 'rgba(237,161,0,0.16)' },
  'Architecture Diagram': { accent: '#008300', tint: 'rgba(0,131,0,0.06)',    chipTint: 'rgba(0,131,0,0.14)' },
  'Workshop':             { accent: '#4a3aa7', tint: 'rgba(74,58,167,0.07)',  chipTint: 'rgba(74,58,167,0.14)' },
};
const DEFAULT_TYPE_STYLE = { accent: '#9a9890', tint: 'rgba(154,152,144,0.06)', chipTint: 'rgba(154,152,144,0.14)' };

const styles = {
  searchBar: {
    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
    borderRadius: '6px', fontSize: '14px', marginBottom: '16px', boxSizing: 'border-box',
  },
  filterRow: { display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' },
  select: {
    padding: '6px 10px', border: '1px solid #ddd',
    borderRadius: '4px', fontSize: '13px', background: '#fff',
  },
  legendRow: {
    display: 'flex', flexWrap: 'wrap', gap: '14px', marginBottom: '18px',
    fontSize: '12px', color: '#666',
  },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: '5px' },
  legendDot: { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  results: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '14px',
  },
  card: {
    borderTop: '1px solid #e5e5e5', borderRight: '1px solid #e5e5e5', borderBottom: '1px solid #e5e5e5',
    borderRadius: '10px', padding: '14px 16px',
    display: 'flex', flexDirection: 'column', gap: '10px',
  },
  cardTitle: { fontSize: '14px', fontWeight: '600', color: '#1a1a1a', lineHeight: 1.35 },
  cardMetaRow: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  badge: {
    display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 9px',
    borderRadius: '999px', fontSize: '11px', fontWeight: '600', color: '#3a3a3a',
  },
  badgeDot: { width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0 },
  tag: {
    display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
    background: '#f0f0f0', fontSize: '11px', color: '#555',
  },
  cardFooter: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: '2px',
  },
  divider: { borderTop: '1px solid #e5e5e5', margin: '28px 0' },
  sectionTitle: { fontSize: '15px', fontWeight: '600', marginBottom: '16px' },
  label: { fontSize: '13px', fontWeight: '600', marginBottom: '6px', display: 'block' },
  input: {
    width: '100%', padding: '8px 10px', border: '1px solid #ddd',
    borderRadius: '4px', fontSize: '13px', marginBottom: '12px', boxSizing: 'border-box',
  },
  btn: {
    padding: '8px 20px', background: '#fd5f0d', color: '#fff',
    border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },
  btnSm: {
    padding: '6px 14px', background: '#fd5f0d', color: '#fff',
    border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  },
  btnDanger: {
    padding: '6px 14px', background: 'none', color: '#c92a2a',
    border: '1px solid #ffc9c9', borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
    marginLeft: '8px',
  },
  uploadBox: {
    border: '2px dashed #ddd', borderRadius: '8px', padding: '28px',
    textAlign: 'center', marginBottom: '16px', cursor: 'pointer', background: '#fafafa',
  },
  successBanner: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '10px 14px', background: '#ebfbee', border: '1px solid #b2f2bb',
    borderRadius: '6px', color: '#2f9e44', fontSize: '13px', marginBottom: '16px',
  },
  errorBanner: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '10px 14px', background: '#fdecea', border: '1px solid #f5c2be',
    borderRadius: '6px', color: '#b00020', fontSize: '13px', marginBottom: '16px',
  },
  fileInfo: { fontSize: '12px', color: '#666', marginTop: '8px' },
  empty: { color: '#999', fontSize: '13px', padding: '20px 0' },
};

function Materials() {
  const { getAccessTokenSilently, user } = useAuth0();
  const roles = user?.[ROLES_CLAIM] || [];
  const isAdmin = roles.includes('Partner Portal Admin');

  // List state
  const [materials, setMaterials] = useState([]);
  const [search, setSearch] = useState('');
  const [product, setProduct] = useState('All');
  const [industry, setIndustry] = useState('All');
  const [type, setType] = useState('All');
  const [listLoading, setListLoading] = useState(true);

  // Upload state (admin)
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadType, setUploadType] = useState('');
  const [uploadProduct, setUploadProduct] = useState('');
  const [uploadIndustry, setUploadIndustry] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadFeedback, setUploadFeedback] = useState({ type: '', msg: '' });
  const fileInputRef = useRef();

  // Request state
  const [reqAssetType, setReqAssetType] = useState('Custom Deck');
  const [reqContext, setReqContext] = useState('');
  const [reqUseCase, setReqUseCase] = useState('');
  const [reqDeadline, setReqDeadline] = useState('');
  const [reqSubmitting, setReqSubmitting] = useState(false);
  const [reqFeedback, setReqFeedback] = useState({ type: '', msg: '' });

  const getToken = async () => getAccessTokenSilently({
    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
  });

  const fetchMaterials = async () => {
    setListLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (product !== 'All') params.set('product', product);
      if (industry !== 'All') params.set('industry', industry);
      if (type !== 'All') params.set('type', type);
      const res = await fetch(`${API_URL}api/materials?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setMaterials(data.materials || []);
    } catch (err) {
      console.error(err);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => { fetchMaterials(); }, [search, product, industry, type]);

  const handleDownload = async (material) => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_URL}api/materials/${material._id}/download`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.downloadUrl) window.open(data.downloadUrl, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this material?')) return;
    try {
      const token = await getToken();
      await fetch(`${API_URL}api/materials/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setMaterials(prev => prev.filter(m => m._id !== id));
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadTitle) return;
    setUploading(true);
    setUploadFeedback({ type: '', msg: '' });
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadTitle);
      formData.append('description', uploadDescription);
      formData.append('type', uploadType);
      formData.append('product', uploadProduct);
      formData.append('industry', uploadIndustry);

      const res = await fetch(`${API_URL}api/materials/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed.');
      setUploadFeedback({ type: 'success', msg: `"${uploadTitle}" uploaded successfully.` });
      setUploadFile(null);
      setUploadTitle('');
      setUploadDescription('');
      setUploadType('');
      setUploadProduct('');
      setUploadIndustry('');
      fetchMaterials();
    } catch (err) {
      setUploadFeedback({ type: 'error', msg: err.message });
    } finally {
      setUploading(false);
    }
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    setReqSubmitting(true);
    setReqFeedback({ type: '', msg: '' });
    try {
      const token = await getToken();
      const res = await fetch(`${API_URL}api/materials/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ assetType: reqAssetType, customerContext: reqContext, useCase: reqUseCase, deadline: reqDeadline }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Submission failed.');
      setReqFeedback({ type: 'success', msg: 'Request submitted! A RAAH team member will follow up.' });
      setReqContext(''); setReqUseCase(''); setReqDeadline('');
    } catch (err) {
      setReqFeedback({ type: 'error', msg: err.message });
    } finally {
      setReqSubmitting(false);
    }
  };

  return (
    <PageShell title="Materials" desc="Search existing assets or request custom materials.">

      {/* Search + Filters */}
      <input style={styles.searchBar} placeholder="Search materials..." value={search} onChange={e => setSearch(e.target.value)} />
      <div style={styles.filterRow}>
        {['product', 'industry', 'type'].map(key => (
          <select key={key} style={styles.select}
            value={key === 'product' ? product : key === 'industry' ? industry : type}
            onChange={e => key === 'product' ? setProduct(e.target.value) : key === 'industry' ? setIndustry(e.target.value) : setType(e.target.value)}>
            {FILTER_OPTIONS[key].map(o => (
              <option key={o} value={o}>{o === 'All' ? FILTER_PLACEHOLDERS[key] : o}</option>
            ))}
          </select>
        ))}
      </div>

      {/* Color legend — type name is the primary signal; color is a scanning aid, never color-alone */}
      {!listLoading && materials.length > 0 && (
        <div style={styles.legendRow}>
          {FILTER_OPTIONS.type.slice(1).map(t => (
            <span key={t} style={styles.legendItem}>
              <span style={{ ...styles.legendDot, background: (TYPE_STYLES[t] || DEFAULT_TYPE_STYLE).accent }} />
              {t}
            </span>
          ))}
        </div>
      )}

      {/* Materials grid */}
      <div style={styles.results}>
        {listLoading ? (
          <div style={styles.empty}>Loading materials…</div>
        ) : materials.length === 0 ? (
          <div style={styles.empty}>No materials found.</div>
        ) : materials.map(m => {
          const ts = TYPE_STYLES[m.type] || DEFAULT_TYPE_STYLE;
          return (
            <div key={m._id} style={{ ...styles.card, background: ts.tint, borderLeft: `4px solid ${ts.accent}` }}>
              <div style={styles.cardTitle}>{m.title}</div>
              <div style={styles.cardMetaRow}>
                {m.type && (
                  <span style={{ ...styles.badge, background: ts.chipTint }}>
                    <span style={{ ...styles.badgeDot, background: ts.accent }} />
                    {m.type}
                  </span>
                )}
                {m.product && <span style={styles.tag}>{m.product}</span>}
                {m.industry && <span style={styles.tag}>{m.industry}</span>}
              </div>
              <div style={styles.cardFooter}>
                {m.downloadCount > 0 ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#1971c2' }}><Download size={12} /> {m.downloadCount}</span>
                ) : <span />}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button style={styles.btnSm} onClick={() => handleDownload(m)}>Download</button>
                  {isAdmin && (
                    <button style={styles.btnDanger} onClick={() => handleDelete(m._id)}>Remove</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Admin upload section */}
      {isAdmin && (
        <>
          <div style={styles.divider} />
          <div style={styles.sectionTitle}>Upload Material</div>

          {uploadFeedback.msg && (
            <div style={uploadFeedback.type === 'success' ? styles.successBanner : styles.errorBanner}>
              {uploadFeedback.type === 'success' ? <CheckCircle2 size={14} /> : <XCircle size={14} />} {uploadFeedback.msg}
            </div>
          )}

          <form onSubmit={handleUpload}>
            <div
              style={{ ...styles.uploadBox, borderColor: uploadFile ? '#fd5f0d' : '#ddd' }}
              onClick={() => fileInputRef.current.click()}
            >
              {uploadFile ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'center', color: '#555' }}><FileText size={24} /></div>
                  <div style={{ fontWeight: '600', marginTop: '8px' }}>{uploadFile.name}</div>
                  <div style={styles.fileInfo}>{(uploadFile.size / 1024 / 1024).toFixed(2)} MB</div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'center', color: '#aaa' }}><UploadCloud size={28} /></div>
                  <div style={{ fontSize: '13px', color: '#888', marginTop: '8px' }}>Click to select a file (max 50MB)</div>
                </>
              )}
              <input ref={fileInputRef} type="file" style={{ display: 'none' }}
                onChange={e => setUploadFile(e.target.files[0] || null)} />
            </div>

            <span style={styles.label}>Title *</span>
            <input style={styles.input} type="text" placeholder="Document title" value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} required />

            <span style={styles.label}>Description <span style={{ fontWeight: '400', color: '#999' }}>optional</span></span>
            <textarea style={{ ...styles.input, resize: 'vertical' }} rows={3}
              placeholder="What's in this document? Shown when it's featured or previewed."
              value={uploadDescription} onChange={e => setUploadDescription(e.target.value)} />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <span style={styles.label}>Type</span>
                <select style={{ ...styles.input, marginBottom: 0 }} value={uploadType} onChange={e => setUploadType(e.target.value)}>
                  <option value="">— Select —</option>
                  {FILTER_OPTIONS.type.slice(1).map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <span style={styles.label}>Product</span>
                <select style={{ ...styles.input, marginBottom: 0 }} value={uploadProduct} onChange={e => setUploadProduct(e.target.value)}>
                  <option value="">— Select —</option>
                  {FILTER_OPTIONS.product.slice(1).map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <span style={styles.label}>Industry</span>
                <select style={{ ...styles.input, marginBottom: 0 }} value={uploadIndustry} onChange={e => setUploadIndustry(e.target.value)}>
                  <option value="">— Select —</option>
                  {FILTER_OPTIONS.industry.slice(1).map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
            </div>

            <button type="submit" style={styles.btn} disabled={uploading}>
              {uploading ? 'Uploading…' : 'Upload Material'}
            </button>
          </form>
        </>
      )}

      {/* Request custom material */}
      <div style={styles.divider} />
      <div style={styles.sectionTitle}>Request a Custom Material</div>

      {reqFeedback.msg && (
        <div style={reqFeedback.type === 'success' ? styles.successBanner : styles.errorBanner}>
          {reqFeedback.type === 'success' ? <CheckCircle2 size={14} /> : <XCircle size={14} />} {reqFeedback.msg}
        </div>
      )}

      <form onSubmit={handleRequest}>
        <span style={styles.label}>Type of Asset</span>
        <select style={styles.input} value={reqAssetType} onChange={e => setReqAssetType(e.target.value)}>
          <option>Custom Deck</option>
          <option>Workshop</option>
          <option>Architecture Diagram</option>
          <option>Positioning Asset</option>
        </select>

        <span style={styles.label}>Customer / Context *</span>
        <input style={styles.input} type="text" placeholder="Customer name or context" value={reqContext} onChange={e => setReqContext(e.target.value)} required />

        <span style={styles.label}>Use Case</span>
        <input style={styles.input} type="text" placeholder="What is this for?" value={reqUseCase} onChange={e => setReqUseCase(e.target.value)} />

        <span style={styles.label}>Deadline</span>
        <input style={styles.input} type="date" value={reqDeadline} onChange={e => setReqDeadline(e.target.value)} />

        <button type="submit" style={styles.btn} disabled={reqSubmitting}>
          {reqSubmitting ? 'Submitting…' : 'Submit Request'}
        </button>
      </form>
    </PageShell>
  );
}

export default Materials;